import Messenger from '../messenger/messenger'

// Interface for analytics event data
export interface AnalyticsEventData {
  [key: string]: unknown
}

interface AnalyticsEventPayload {
  event: string
  data: AnalyticsEventData
}

interface TealiumAnalyticsConfig {
  account: string
  campaignId: string
  environment: string
  hasCartEnabled?: boolean | string
  orgId: string
  profile: string
}

interface TealiumAnalyticsEventPayload {
  config: TealiumAnalyticsConfig
  data: AnalyticsEventData
  event: string
  eventType: 'page' | 'track'
}

interface AnalyticsManagerProps {
  win: Window
}

export default class AnalyticsManager {
  private props: AnalyticsManagerProps
  private GA4_ID: string | null = null
  private analyticsLoaded = false
  private eventQueue: Array<{ eventName: string; eventData: AnalyticsEventData }> = []
  private tealiumInitialized = false
  private tealiumEventQueue: TealiumAnalyticsEventPayload[] = []
  private messenger: Messenger | null = null
  private destroyed = false

  constructor(props: AnalyticsManagerProps) {
    this.props = props
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    this.processEventQueue()
    this.processTealiumEventQueue()

    if (this.messenger) {
      this.messenger.destroy()
      this.messenger = null
    }

    this.eventQueue = []
    this.tealiumEventQueue = []
    this.analyticsLoaded = false
    this.tealiumInitialized = false
  }

  public async initialize(): Promise<void> {
    // Start by checking if GA4 or GTM is already available, otherwise fetch analytics
    await this.initializeAnalytics()
  }

  /**
   * Checks if GA4 is available either directly via gtag.js or indirectly via GTM.
   */
  private isGA4Available(): boolean {
    return typeof window.gtag === 'function' || this.checkGA4InDataLayer()
  }

  /**
   * Checks if Google Tag Manager (GTM) is available by verifying the presence of the 'dataLayer'.
   */
  private isGTMAvailable(): boolean {
    const gtmStartedEvent = window?.dataLayer?.find((element) => element['gtm.start'])

    if (!gtmStartedEvent) {
      return false // GTM has not started
    }

    if (!gtmStartedEvent['gtm.uniqueEventId']) {
      return false // GTM config ran, but main GTM JS has not loaded (AdBlock, NoScript, etc.)
    }

    return true // GTM is fully loaded and operational
  }

  /**
   * Helper function to check if GA4 has been set up via GTM.
   */
  private checkGA4InDataLayer(): boolean {
    if (window.dataLayer) {
      for (const entry of window.dataLayer) {
        if (entry[0] === 'config' && entry[1] === this.GA4_ID) {
          return true
        }
      }
    }

    return false
  }

  private isTealiumAvailable(): boolean {
    return (
      typeof this.props.win.utag?.link === 'function' &&
      typeof this.props.win.utag?.view === 'function'
    )
  }

  private hasValidTealiumConfig(config: TealiumAnalyticsConfig): boolean {
    return Boolean(
      config?.account?.trim() && config?.profile?.trim() && config?.environment?.trim()
    )
  }

  private initializeTealium(config: TealiumAnalyticsConfig): void {
    if (this.tealiumInitialized || !this.hasValidTealiumConfig(config)) {
      return
    }

    const win = this.props.win
    const doc = win.document
    const { account, profile, ...superProperties } = config

    win.utag_cfg_ovrd = win.utag_cfg_ovrd || {}
    win.utag_cfg_ovrd.noview = true
    win.utag_data = superProperties

    if (!doc.getElementById('classy-tealium-sync-script')) {
      const syncScript = doc.createElement('script')
      syncScript.id = 'classy-tealium-sync-script'
      syncScript.type = 'text/javascript'
      syncScript.src = `https://tags.tiqcdn.com/utag/${account}/${profile}/${config.environment}/utag.sync.js`
      doc.head.prepend(syncScript)
    }

    if (!doc.getElementById('classy-tealium-script')) {
      const tealiumScript = doc.createElement('script')
      tealiumScript.id = 'classy-tealium-script'
      tealiumScript.type = 'text/javascript'
      tealiumScript.async = true
      tealiumScript.src = `https://tags.tiqcdn.com/utag/${account}/${profile}/${config.environment}/utag.js`
      tealiumScript.addEventListener('load', () => this.processTealiumEventQueue())
      doc.body.prepend(tealiumScript)
    }

    this.tealiumInitialized = true
  }

  /**
   * Initializes analytics by checking if GA4 or GTM are already available.
   * If neither is available, it queues events and checks availability each time an event comes in.
   */
  private async initializeAnalytics(): Promise<void> {
    try {
      // Check if GA4 or GTM are already available
      const ga4Available = this.isGA4Available()
      const gtmAvailable = this.isGTMAvailable()

      if (ga4Available || gtmAvailable) {
        if (process.env.ENV !== 'production') {
          console.log('GA4 or GTM is already available. Skipping fetch for analytics settings.')
        }

        this.analyticsLoaded = true

        this.processEventQueue() // Process any queued events

        return
      }
    } catch (error) {
      console.error('Failed to initialize analytics:', error)
    }
  }

  /**
   * Queues events if GA4/GTM are not yet loaded, or sends them if they are.
   * If GA4/GTM are not available, checks availability each time an event is queued.
   *
   * @param {string} eventName - The name of the event.
   * @param {AnalyticsEventData} eventData - The data associated with the event.
   */
  private queueOrSendEvent(eventName: string, eventData: AnalyticsEventData): void {
    const isProd = process.env.ENV === 'production'

    if (this.analyticsLoaded) {
      this.handleAnalyticsEvent(eventName, eventData)
    } else {
      // Check for GA4 or GTM availability whenever an event is queued
      const ga4Available = this.isGA4Available()
      const gtmAvailable = this.isGTMAvailable()

      if (ga4Available || gtmAvailable) {
        if (!isProd) {
          console.log('GA4 or GTM is now available. Processing queued events...')
        }

        this.analyticsLoaded = true // Mark analytics as loaded

        this.processEventQueue() // Process any queued events
        this.handleAnalyticsEvent(eventName, eventData) // Send the current event immediately
      } else {
        if (!isProd) {
          console.log('GA4 or GTM not available. Queueing event:', { eventName, eventData })
        }

        this.eventQueue.push({ eventName, eventData })
      }
    }
  }

  /**
   * Processes queued events once GA4/GTM are loaded.
   */
  private processEventQueue(): void {
    while (this.eventQueue.length > 0) {
      const eventItem = this.eventQueue.shift()

      if (eventItem) {
        const { eventName, eventData } = eventItem

        this.handleAnalyticsEvent(eventName, eventData)
      }
    }
  }

  private queueOrSendTealiumEvent(payload: TealiumAnalyticsEventPayload): void {
    if (!this.hasValidTealiumConfig(payload.config)) {
      console.warn('Tealium analytics config is missing account, profile, or environment.')
      return
    }

    this.initializeTealium(payload.config)

    if (this.isTealiumAvailable()) {
      this.processTealiumEventQueue()
      this.sendEventToTealium(payload)
      return
    }

    this.tealiumEventQueue.push(payload)
  }

  private processTealiumEventQueue(): void {
    if (!this.isTealiumAvailable()) {
      return
    }

    while (this.tealiumEventQueue.length > 0) {
      const eventItem = this.tealiumEventQueue.shift()

      if (eventItem) {
        this.sendEventToTealium(eventItem)
      }
    }
  }

  private sendEventToTealium(payload: TealiumAnalyticsEventPayload): void {
    const { utag } = this.props.win

    if (!utag?.link || !utag.view) {
      console.warn('Tealium is not available to handle the event.')
      return
    }

    if (payload.eventType === 'page') {
      utag.view(payload.data)
      return
    }

    utag.link(payload.data)
  }

  /**
   * Sends an event to GA4 using gtag.js.
   * @param {string} eventName - The name of the event to send.
   * @param {AnalyticsEventData} eventData - The data associated with the event.
   */
  private sendEventToGA4(eventName: string, eventData: AnalyticsEventData): void {
    if (this.isGA4Available()) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, eventData)
      } else {
        console.warn('GA4 is not available to handle the event.')
      }
    }
  }

  /**
   * Sends an event to GTM by pushing it into the dataLayer.
   * @param {string} eventName - The name of the event to send.
   * @param {AnalyticsEventData} eventData - The data associated with the event.
   */
  private sendEventToGTM(eventName: string, eventData: AnalyticsEventData): void {
    if (this.isGTMAvailable()) {
      window.dataLayer.push({
        event: eventName,
        ...eventData,
      })
    } else {
      console.warn('GTM is not available to handle the event.')
    }
  }

  /**
   * Handles an analytics event by routing it to either GA4 or GTM.
   * @param {string} eventName - The name of the event.
   * @param {AnalyticsEventData} eventData - The data associated with the event.
   */
  private handleAnalyticsEvent(eventName: string, eventData: AnalyticsEventData): void {
    if (this.isGA4Available()) {
      this.sendEventToGA4(eventName, eventData)
    } else if (this.isGTMAvailable()) {
      this.sendEventToGTM(eventName, eventData)
    } else {
      console.warn('Neither GA4 nor GTM are available to handle the event.')
    }
  }

  public setupEventListener(): void {
    this.messenger = new Messenger({ win: this.props.win })

    this.messenger.onAnalyticsEvent((event) => {
      const { event: eventName, data } = event.data.payload as AnalyticsEventPayload
      this.queueOrSendEvent(eventName, data)
    })

    this.messenger.onTealiumAnalyticsEvent((event) => {
      this.queueOrSendTealiumEvent(event.data.payload as TealiumAnalyticsEventPayload)
    })
  }

  /**
   * NOTE: TODO: We'll likely want to pass this event back across the iframe boundary
   * to the parent window for our own tracking purposes.
   *
   * Public method to send an event to GA4 or GTM directly.
   * This can be used to send events from the SDK.
   *
   * @param {string} eventName - The name of the event to send.
   * @param {AnalyticsEventData} eventData - The data associated with the event.
   */
  public sendEvent(eventName: string, eventData: AnalyticsEventData): void {
    this.queueOrSendEvent(eventName, eventData)
  }
}
